import {
  FormInputController,
  WajeezFormInputV2,
} from '@wajeez-web/components/form-builder';
import { Autocomplete } from '@wajeez-web/components/mui';
import { Control } from 'react-hook-form';
import { AiFillDelete } from 'react-icons/ai';
import { useContentSelector } from '@wajeez-web/stores/content';
import { Server } from '@wajeez-web/core/api-base';
import { useContentEntityListsLookups } from '@wajeez-web/components/helpers';

import { FormInputMultilingual } from './CollectionComponentFormBuilder';

interface SelectFactorsProps {
  selectFactorTemplate: Server.SelectFactorOption | undefined;
  removeSelectFactor: (index?: number | number[] | undefined) => void;
  control: Control<FormInputMultilingual, any>;
  index: number;
}

export const SelectFactor = ({
  selectFactorTemplate,
  removeSelectFactor,
  control,
  index,
}: SelectFactorsProps) => {
  const contentEntityTypeLookups = useContentSelector(
    ({ contentEntityType }) => contentEntityType.lookups
  );

  return (
    <div className="mt-3 p-4 flex justify-center items-center rounded border">
      <div className="w-full">
        <div className="w-full font-medium">{selectFactorTemplate?.name}</div>
        <div className="mt-4 w-full grid grid-cols-2 gap-10">
          {selectFactorTemplate?.defaultEntityId && (
            <WajeezFormInputV2
              control={control}
              name={`selectors.${index}.defaultEntityId`}
              fieldMeta={{
                fieldType: Server.FieldType.Text,
                fieldOptions: {},
                label: 'Default Entity',
                placeHolder: 'Default Entity',
              }}
              id={`selectors.${index}.defaultEntityId`}
            />
          )}
          {selectFactorTemplate?.defaultTypeId && (
            <FormInputController
              control={control}
              name={`selectors.${index}.defaultTypeId`}
              fieldMeta={{
                fieldType: Server.FieldType.Select,
                fieldOptions: {},
              }}
              renderInput={({ ...props }) => (
                <Autocomplete
                  id={`default-type-autocomplete-${index}`}
                  options={contentEntityTypeLookups.map((c) => {
                    return {
                      value: c.id,
                      label: c.displayNamePlural + ` (${c.name})`,
                    };
                  })}
                  label="Content Entity Type"
                  color="#1f7983"
                  {...props}
                />
              )}
            />
          )}
          {selectFactorTemplate?.defaultRelationLength && (
            <WajeezFormInputV2
              control={control}
              name={`selectors.${index}.defaultRelationLength`}
              fieldMeta={{
                fieldType: Server.FieldType.Number,
                fieldOptions: {},
                label: 'Default Relation Length',
                placeHolder: 'Default Relation Length',
              }}
              id={`selectors.${index}.defaultRelationLength`}
            />
          )}
          {selectFactorTemplate?.defaultCritiria && (
            <SelectFactorDefaultCriteria
              control={control}
              selectFactor={selectFactorTemplate.factor}
              selectedSelectorIndex={index}
            />
          )}
        </div>
      </div>
      <div
        className="mr-[20px] text-red-500 cursor-pointer hover:opacity-75 active:opacity-100"
        onClick={() => {
          removeSelectFactor(index);
        }}
      >
        <AiFillDelete />
      </div>
    </div>
  );
};

interface SelectFactorDefaultCriteriaProps {
  selectFactor: Server.SelectFactor | undefined;
  selectedSelectorIndex: number;
  control: Control<FormInputMultilingual, any>;
}

const SelectFactorDefaultCriteria = ({
  selectFactor,
  selectedSelectorIndex,
  control,
}: SelectFactorDefaultCriteriaProps) => {
  const contentEntityTypeLookups = useContentSelector(
    ({ contentEntityType }) => contentEntityType.lookups
  );

  const contentEntityListsLookups = useContentEntityListsLookups();

  switch (selectFactor) {
    case Server.SelectFactor.OneOfTypes:
      return (
        <div className="w-[400px]">
          <FormInputController
            control={control}
            name={`selectors.${selectedSelectorIndex}.defaultCritiria`}
            fieldMeta={{
              fieldType: Server.FieldType.Select,
              fieldOptions: {},
            }}
            renderInput={({ ...props }) => (
              <Autocomplete
                id={`default-type-autocomplete-${selectedSelectorIndex}`}
                options={contentEntityTypeLookups.map((c) => {
                  return {
                    value: c.id,
                    label: c.displayNamePlural + ` (${c.name})`,
                  };
                })}
                label="Content Entity Types"
                color="#1f7983"
                heightClassName="h-auto"
                multiple
                value={JSON.parse(props.value ?? '[]')}
                onChange={(v) => {
                  props.onChange(JSON.stringify(v));
                }}
                isError={props.isError}
              />
            )}
          />
        </div>
      );
    case Server.SelectFactor.ContentList:
    case Server.SelectFactor.NotInContentList:
      return (
        <div className="w-[400px]">
          <FormInputController
            control={control}
            name={`selectors.${selectedSelectorIndex}.defaultCritiria`}
            fieldMeta={{
              fieldType: Server.FieldType.Select,
              fieldOptions: {},
            }}
            renderInput={({ ...props }) => (
              <Autocomplete
                id={`default-type-autocomplete-${selectedSelectorIndex}`}
                options={contentEntityListsLookups.data.map((c) => {
                  return {
                    value: c.id?.toString(),
                    label: c.referenceName,
                  };
                })}
                label="Content Entity Lists"
                color="#1f7983"
                {...props}
              />
            )}
          />
        </div>
      );
    default:
      return (
        <WajeezFormInputV2
          control={control}
          name={`selectors.${selectedSelectorIndex}.defaultCritiria`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            fieldOptions: {},
            label: 'Default Criteria',
            placeHolder: 'Default Criteria',
          }}
          id={`selectors.${selectedSelectorIndex}.defaultCriteria`}
        />
      );
  }
};
